import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../services/apiClient';
import { ShieldCheck, ArrowRight, Sparkles, LayoutDashboard, Target, User, Mail, Key, Briefcase, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next'; // IMPORT I18N

export default function RegisterPage() {
    const { t } = useTranslation(); // INIT I18N
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        full_name: '',
        objective: ''
    });

    const handleApply = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg('');

        try {
            const response = await api.post('/auth/register', formData);
            if (response.status === 200 || response.status === 201) {
                setIsSubmitted(true);
            }
        } catch (error) {
            console.error("Erreur inscription:", error);
            setErrorMsg(t('register.error_msg')); // MESSAGE TRADUIT
        } finally {
            setLoading(false);
        }
    };

    const FeatureItem = ({ icon: Icon, text }) => (
        <div className="flex items-center gap-4 text-slate-300 bg-white/5 p-3 rounded-2xl border border-white/5">
            <div className="p-2.5 bg-indigo-500/20 rounded-xl text-indigo-400"><Icon size={20} /></div>
            <span className="text-sm font-bold">{text}</span>
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row font-sans pt-14">
            
            {/* --- COLONNE GAUCHE (MARKETING EXCLUSIF) --- */}
            <div className="w-full md:w-5/12 bg-slate-900 text-white p-8 md:p-14 flex flex-col justify-between relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5"></div>
                <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-indigo-600 rounded-full blur-[120px] opacity-40 pointer-events-none"></div>
                
                <div className="relative z-10">
                    <Link to="/" className="flex items-center gap-3 mb-16 opacity-90 hover:opacity-100 transition-opacity">
                        <div className="h-10 w-10 bg-white rounded-xl flex items-center justify-center text-slate-900 font-black text-xl shadow-lg">K</div>
                        <span className="font-black tracking-widest uppercase text-sm">{t('register.club')}</span>
                    </Link>

                    <h1 className="text-4xl lg:text-5xl font-black mb-6 leading-tight tracking-tight">
                        {t('register.title_p1')} <br/> <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-emerald-400">{t('register.title_p2')}</span>
                    </h1>
                    
                    <p className="text-slate-400 mb-10 leading-relaxed font-medium text-lg max-w-md">
                        {t('register.subtitle')}
                    </p>

                    <div className="space-y-4 mb-12 max-w-md">
                        <FeatureItem icon={LayoutDashboard} text={t('register.feat1')} />
                        <FeatureItem icon={Target} text={t('register.feat2')} />
                        <FeatureItem icon={Sparkles} text={t('register.feat3')} />
                    </div>
                </div>

                <div className="relative z-10 bg-indigo-950/50 backdrop-blur-md p-6 rounded-3xl border border-indigo-500/20">
                    <div className="flex items-center gap-4">
                        <ShieldCheck className="text-indigo-400" size={32} />
                        <div>
                            <p className="text-xs text-indigo-300 font-black uppercase tracking-wider mb-1">{t('register.process_title')}</p>
                            <p className="text-sm font-medium text-slate-300">{t('register.process_desc')}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- COLONNE DROITE (FORMULAIRE D'ADMISSION) --- */}
            <div className="w-full md:w-7/12 p-6 md:p-12 flex items-center justify-center overflow-y-auto bg-slate-50">
                <div className="w-full max-w-lg">
                    
                    {isSubmitted ? (
                        <div className="bg-white p-10 rounded-3xl border border-slate-200 shadow-xl text-center animate-in zoom-in-95 duration-500">
                            <div className="w-24 h-24 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                <CheckCircle2 size={48} className="text-emerald-500" />
                            </div>
                            <h2 className="text-3xl font-black text-slate-900 mb-4">{t('register.success_title')}</h2>
                            <p className="text-slate-500 mb-8 font-medium leading-relaxed">
                                {t('register.success_desc')}
                            </p>
                            <Link to="/" className="inline-block bg-slate-900 text-white px-8 py-4 rounded-xl font-bold hover:bg-slate-800 transition-colors shadow-lg">
                                {t('register.btn_home')}
                            </Link>
                        </div>
                    ) : (
                        <div className="bg-white p-8 md:p-10 rounded-3xl border border-slate-200 shadow-xl animate-in fade-in slide-in-from-bottom-8 duration-500">
                            <h2 className="text-3xl font-black text-slate-900 mb-2">{t('register.form_title')}</h2>
                            <p className="text-slate-500 mb-8 font-medium">{t('register.form_subtitle')}</p>

                            {errorMsg && <div className="mb-6 p-4 bg-rose-50 text-rose-700 rounded-xl text-sm font-bold border border-rose-100 flex items-center gap-2"><AlertTriangle size={18}/> {errorMsg}</div>}

                            <form onSubmit={handleApply} className="space-y-5">
                                <div>
                                    <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2">{t('register.label_name')}</label>
                                    <div className="relative">
                                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20}/>
                                        <input type="text" required className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-bold text-slate-800" placeholder={t('register.place_name')} value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2">{t('register.label_email')}</label>
                                    <div className="relative">
                                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20}/>
                                        <input type="email" required className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-bold text-slate-800" placeholder={t('register.place_email')} value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-2">{t('register.label_pwd')}</label>
                                    <div className="relative">
                                        <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20}/>
                                        <input type="password" required className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-bold text-slate-800" placeholder={t('register.place_pwd')} value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-black text-indigo-500 uppercase tracking-wider mb-2 flex items-center gap-2"><Briefcase size={14}/> {t('register.label_obj')}</label>
                                    <textarea className="w-full p-4 bg-indigo-50/50 border border-indigo-100 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all font-medium text-slate-700 min-h-[100px] resize-none" placeholder={t('register.place_obj')} value={formData.objective} onChange={e => setFormData({...formData, objective: e.target.value})}></textarea>
                                </div>

                                <button type="submit" disabled={loading} className="w-full mt-4 bg-indigo-600 text-white py-4 rounded-2xl font-black flex items-center justify-center gap-3 hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-200 disabled:opacity-70 disabled:scale-100 hover:-translate-y-1">
                                    {loading ? t('register.btn_loading') : <>{t('register.btn_submit')} <ArrowRight size={20}/></>}
                                </button>
                                
                                <div className="pt-6 text-center border-t border-slate-100 mt-6">
                                    <span className="text-sm font-medium text-slate-500">{t('register.already_member')} </span>
                                    <Link to="/login" className="text-sm font-black text-indigo-600 hover:text-indigo-800 transition-colors">{t('register.login_here')}</Link>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
